import { useEffect, useRef, useState } from "react";
import { Loader2, X, Search, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * RF-03 — Selector de códigos UNSPSC validados contra datos.gov.co (SECOP II).
 * - Búsqueda por término (ej. "papelería") → agrega códigos reales usados en Colombia.
 * - Validación por código ingresado directo.
 * - Multi-selección hasta `max`.
 * - `value` es un array de strings `["44121700", ...]`.
 */
export function UnspscPicker({ value = [], onChange, max = 5 }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitationMsg, setLimitationMsg] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [resultDetails, setResultDetails] = useState({}); // code -> {name}
  const abortRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];

  // Debounce search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setError("");
      setLimitationMsg("");
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await fetch(
          `${API_BASE}/api/public/unspsc/search?q=${encodeURIComponent(q)}&limit=10`,
          { signal: ctrl.signal, credentials: "include" }
        );
        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(txt || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        setResults(data.items || []);
        setLimitationMsg(data.limitation || "");
        // store names for display when selected
        setResultDetails((prev) => {
          const next = { ...prev };
          (data.items || []).forEach((it) => {
            next[it.code] = { name: it.name };
          });
          return next;
        });
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(e.message || "Error consultando datos.gov.co");
        }
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query]);

  const addCode = (code) => {
    if (selected.includes(code)) return;
    if (selected.length >= max) return;
    onChange([...selected, code]);
  };

  const removeCode = (code) => {
    onChange(selected.filter((c) => c !== code));
  };

  const validateManual = async () => {
    const code = manualCode.trim();
    if (!code) return;
    setValidating(true);
    setError("");
    try {
      const resp = await fetch(
        `${API_BASE}/api/public/unspsc/validate/${encodeURIComponent(code)}`,
        { credentials: "include" }
      );
      if (resp.status === 404) {
        setError("Código no encontrado en datos.gov.co");
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (!data.found) {
        const reasonMsg = {
          formato_invalido: "Formato de código inválido. Debe tener 6 a 10 dígitos.",
          no_encontrado_en_secop: "Código no encontrado en los procesos SECOP II registrados.",
        }[data.reason] || `Código inválido (${data.reason || "no encontrado"})`;
        setError(reasonMsg);
        return;
      }
      const name = (data.samples && data.samples[0]?.nombre_procedimiento) || "";
      setResultDetails((p) => ({ ...p, [data.code]: { name } }));
      addCode(data.code);
      setManualCode("");
    } catch (e) {
      setError(e.message || "Error consultando datos.gov.co");
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="unspsc-picker">
      {/* Buscar por descripción */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por término (ej. papelería, software, obra civil...)"
          className="pl-9 rounded-sm h-11"
          data-testid="unspsc-search-input"
          disabled={selected.length >= max}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 p-2 rounded-sm" data-testid="unspsc-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {limitationMsg && !results.length && !loading && (
        <p className="text-xs text-gray-500 italic" data-testid="unspsc-limitation">
          {limitationMsg}
        </p>
      )}

      {results.length > 0 && (
        <ul className="border border-gray-200 bg-white max-h-56 overflow-y-auto divide-y divide-gray-100" data-testid="unspsc-results">
          {results.map((it) => {
            const isSelected = selected.includes(it.code);
            return (
              <li key={it.code} className="px-3 py-2 hover:bg-gray-50">
                <button
                  type="button"
                  onClick={() => addCode(it.code)}
                  disabled={isSelected || selected.length >= max}
                  className="w-full text-left disabled:opacity-50"
                  data-testid={`unspsc-result-${it.code}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-primary">
                        {it.code}
                      </div>
                      <div className="text-xs text-gray-700 truncate">
                        {it.name || "— sin nombre registrado —"}
                      </div>
                    </div>
                    <div className="shrink-0 text-xs text-gray-500">
                      {it.uses_in_secop} uso{it.uses_in_secop === 1 ? "" : "s"} en SECOP
                      {isSelected && (
                        <span className="ml-2 text-primary font-medium">✓</span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Añadir manual por código */}
      <div className="flex gap-2 items-start pt-1">
        <div className="flex-1">
          <Input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="O ingresa el código UNSPSC exacto (8 dígitos)"
            className="rounded-sm h-10 font-mono"
            data-testid="unspsc-manual-input"
            disabled={selected.length >= max}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={validateManual}
          disabled={validating || !manualCode.trim() || selected.length >= max}
          className="h-10 rounded-sm"
          data-testid="unspsc-manual-add"
        >
          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validar y añadir"}
        </Button>
      </div>

      {/* Chips seleccionados */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          {selected.map((code) => (
            <Badge
              key={code}
              variant="secondary"
              className="pl-3 pr-1 py-1 font-mono flex items-center gap-1"
              data-testid={`unspsc-selected-${code}`}
            >
              <span>{code}</span>
              {resultDetails[code]?.name && (
                <span className="font-sans font-normal text-xs text-gray-500 ml-1 max-w-[180px] truncate">
                  — {resultDetails[code].name}
                </span>
              )}
              <button
                type="button"
                onClick={() => removeCode(code)}
                className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                aria-label={`Eliminar ${code}`}
                data-testid={`unspsc-remove-${code}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <p className="text-[11px] text-gray-500">
        Fuente oficial: SECOP II en datos.gov.co. Máximo {max} códigos. {selected.length}/{max} seleccionados.
      </p>
    </div>
  );
}

export default UnspscPicker;
